import AddIcon from '@mui/icons-material/Add';
import { Box, Stack, Tooltip } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import {
  type GridColDef,
  type GridRowsProp,
} from '@mui/x-data-grid';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import RouterDelete from './RouterDelete.js';
import RouterForm from './RouterForm.js';
import { CARRIER_LABELS } from './routerSimForm.js';
import { useAllRouters } from '../../api/index.js';
import { useCheckPermissions } from '../../auth/index.js';
import useDialogControls from '../../hooks/use-dialog-controls.js';
import { type Router } from '../../models/index.js';
import Table from '../Table/Table.js';
import tableActions from '../Table/tableActions.js';

const VISIBLE_FIELDS = ['patientName', 'name', 'ipAddress', 'network', 'cameras', 'actions'];

const NETWORK_STATE_LABELS = {
  online: 'Online',
  offline: 'Offline',
  degraded: 'Unstable',
  switching: 'Switching SIM',
} as const;

export function networkLabel({ networkState, activeSim }: Pick<Router, 'networkState' | 'activeSim'>): string {
  if (!networkState) {
    return 'Not checked yet';
  }
  if (!activeSim) {
    return NETWORK_STATE_LABELS[networkState];
  }
  const backup = activeSim.isPrimary ? '' : ' (backup)';
  return `${NETWORK_STATE_LABELS[networkState]} · ${CARRIER_LABELS[activeSim.carrier]}${backup}`;
}

const RoutersTable = () => {
  const { data = [], isLoading, isError, refetch } = useAllRouters();
  const {open: formOpen, selectedData: formData, onOpen: onFormOpen, reset: resetForm, setOpen: setFormOpen} = useDialogControls<Router>(data);
  const {open: deleteOpen, selectedData: deleteData, onOpen: onDelete, reset: resetDelete} = useDialogControls<Router>(data);

  const { canManageRouter, canDeleteRouter } = useCheckPermissions();
  const navigate = useNavigate();

  const columns: GridColDef[] = useMemo(() => {
    const cols: GridColDef[] = [
      { field: 'patientName', headerName: 'Patient Name', flex: 1 },
      { field: 'name', headerName: 'Name', flex: 1 },
      { field: 'ipAddress', headerName: 'IP Address', flex: 1 },
      { field: 'network', headerName: 'Network', flex: 1 },
      { field: 'cameras', headerName: 'Cameras', flex: 1 },
    ];

    if (canManageRouter || canDeleteRouter) {
      cols.push({
        field: 'actions',
        headerName: 'Actions',
        width: 100,
        sortable: false,
        type: 'actions',
        getActions: (params) => tableActions(onDelete(params.id), onFormOpen(params.id))({
          canEdit: canManageRouter,
          canDelete: canDeleteRouter,
        }),
      });
    }
    // Otherwise filter will be applied on fields such as the hidden column id
    return cols.filter((column) => VISIBLE_FIELDS.includes(column.field));
  }, [canManageRouter, canDeleteRouter, onFormOpen, onDelete]);

  const rows: GridRowsProp = useMemo(() => {
    return data.map(({ id, name, patient, ipAddress, cameras, networkState, activeSim }) => {
      return {
        id,
        patientName: `${patient?.firstName} ${patient?.lastName}`,
        name,
        ipAddress,
        network: networkLabel({ networkState, activeSim }),
        cameras: (cameras || []).map((camera) => camera.name).join(', '),
      };
    });
  }, [data]);

  if (isError) {
    return <h1>Error loading routers</h1>;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction={'row'} justifyContent={'space-between'} sx={{ my: 1 }}>
        <h2 style={{ margin: 0 }}>Routers</h2>
        <Tooltip arrow color="primary" placement="bottom" title={'Add a new router'}>
          <IconButton color="inherit" aria-label="Add a new router" onClick={() => setFormOpen(true)} >
            <AddIcon />
          </IconButton>
        </Tooltip>
      </Stack>
      <Table
        columns={columns}
        rows={rows}
        NoRowsOverlayText={'No routers available.'}
        isLoading={isLoading}
        pageSize={10}
        pageSizeOptions={[10, 25, 50, 100]}
        toolbar
        onCellClick={(params) => params.field !== 'actions' && navigate(`/routers/${params.id}`)}
      />
      <RouterForm
        open={formOpen}
        onClose={resetForm}
        routerId={formData?.id}
        refetch={refetch}
      />
      <RouterDelete
        open={deleteOpen}
        onClose={resetDelete}
        routerId={deleteData?.id}
        refetch={refetch}
      />
    </Box>
  );
};

export default RoutersTable;
